import { useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { EATING_WAY_BACKGROUND_SETTING } from 'utils';
import './style.scss';

export interface SelectBackgroundModalProps {
  title: string;
  subtitle: string;
  bgcolor1?: string;
  bgcolor2?: string;
  defaultBgColor1: string;
  defaultBgColor2: string;
  onClose?: () => void;
  onReflect?: (bgColor1: string, bgColor2: string) => void;
}

export function SelectBackgroundModal({
  title,
  subtitle,
  bgcolor1 = EATING_WAY_BACKGROUND_SETTING.main.genre.color1,
  bgcolor2 = EATING_WAY_BACKGROUND_SETTING.main.genre.color2,
  defaultBgColor1,
  defaultBgColor2,
  onClose,
  onReflect,
}: SelectBackgroundModalProps) {
  const { t } = useTranslation();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // refs
  const colorInputRef1 = useRef<HTMLInputElement>(null);
  const colorInputRef2 = useRef<HTMLInputElement>(null);

  // state
  const [selectedColor1, setSelectedColor1] = useState(bgcolor1);
  const [selectedColor2, setSelectedColor2] = useState(bgcolor2);

  function handleClose() {
    onClose?.();
  }

  function handleColor1Click() {
    if (colorInputRef1.current) {
      colorInputRef1.current.click();
    }
  }

  function handleColor2Click() {
    if (colorInputRef2.current) {
      colorInputRef2.current.click();
    }
  }

  function handleReset() {
    setSelectedColor1(defaultBgColor1);
    setSelectedColor2(defaultBgColor2);
  }

  function handleSave() {
    onReflect?.(selectedColor1, selectedColor2);
    handleClose();
  }

  return (
    <Modal size="lg" dialogClassName="select-background-modal" show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('SelectBackgroundModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <div className="mb-2 fw-bold">{title}</div>
        <div className="mb-2 fw-bold">{subtitle}</div>
        <div className="px-3">
          <Row className="mb-4">
            <Col className="d-flex align-items-center">
              <strong>{t('SelectBackgroundModal.color_setting')}</strong>
            </Col>
            <Col className="text-end">
              <Button className="btn--dark-orange" onClick={handleReset}>
                {t('SelectBackgroundModal.reset')}
              </Button>
              <Button className="btn--dark-green ms-4" onClick={handleSave}>
                {t('SelectBackgroundModal.save')}
              </Button>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <Row>
                <Col sm={7}>
                  <div
                    className="px-3 py-2 text-center"
                    style={{
                      background: selectedColor1,
                      color: '#fff',
                      height: '29px',
                    }}
                  >
                    {t('SelectBackgroundModal.color1')}
                  </div>
                </Col>
                <Col sm={5}>
                  <Button className="w-100" onClick={handleColor1Click}>
                    {t('SelectBackgroundModal.change')}
                  </Button>
                  <Form.Control
                    ref={colorInputRef1}
                    type="color"
                    className="invisible"
                    style={{ height: 0, padding: 0 }}
                    value={selectedColor1}
                    onChange={(event) => {
                      setSelectedColor1(event.target.value);
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Col sm={7}>
                  <div
                    className="px-3 py-2 text-center"
                    style={{
                      background: selectedColor2,
                      color: '#fff',
                      height: '29px',
                    }}
                  >
                    {t('SelectBackgroundModal.color2')}
                  </div>
                </Col>
                <Col sm={5}>
                  <Button className="w-100" onClick={handleColor2Click}>
                    {t('SelectBackgroundModal.change')}
                  </Button>
                  <Form.Control
                    ref={colorInputRef2}
                    type="color"
                    className="invisible"
                    style={{ height: 0, padding: 0 }}
                    value={selectedColor2}
                    onChange={(event) => {
                      setSelectedColor2(event.target.value);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <div>
            <strong>{t('SelectBackgroundModal.preview')}</strong>
          </div>
          <div className="preview-bar px-3">
            <div
              className="px-3 py-2 text-center"
              style={{
                background: `linear-gradient(to right, ${selectedColor1} 0%, ${selectedColor2} 100%)`,
                color: '#fff',
                height: '29px',
              }}
            >
              &nbsp;
            </div>
            <div className="guide-nav guide-left">
              <div className="arrow-up"></div>
              <div>{t('SelectBackgroundModal.color1')}</div>
            </div>
            <div className="guide-nav guide-right">
              <div className="arrow-up"></div>
              <div>{t('SelectBackgroundModal.color2')}</div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
